import { DataTable } from '@/components/DataTable';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { AuditLogInsertInput, Device, Site } from '@/generated/graphql';
import { execute } from '@/graphql/execute';
import useDataTable from '@/hooks/useDataTable';
import { AuditLogAction, AuditLogEntityType } from '@/models/audit-log.models';
import { createAuditLogInput } from '@/utils/audit-log.function';
import { DialogDescription } from '@radix-ui/react-dialog';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  ColumnDef,
  PaginationState,
  SortingState,
} from '@tanstack/react-table';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { assignedDevicesToSiteMutation } from '../graphql/mutation';
import { unassignedDeviceQuery } from '../graphql/query';

const tableColumns: ColumnDef<Device>[] = [
  {
    id: 'select',
    header: ({ table }) => (
      <Checkbox
        checked={
          table.getIsAllPageRowsSelected() ||
          (table.getIsSomePageRowsSelected() && 'indeterminate')
        }
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label="Select all"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label="Select row"
      />
    ),
    size: 50,
  },
  {
    accessorKey: 'name',
    header: 'Name',
  },
  {
    accessorKey: 'serial',
    header: 'Serial',
  },
];

interface AssignDeviceToSiteProps {
  open: boolean;
  site: Site;
  onToggleOpen: (refetch?: boolean) => void;
}

export function AssignDeviceToSiteDialog({
  open,
  site,
  onToggleOpen,
}: AssignDeviceToSiteProps) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [rowCount, setRowCount] = useState(0);
  const [devices, setDevices] = useState<Device[]>([]);

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 50,
  });

  const table = useDataTable({
    data: devices,
    columns: tableColumns,
    rowCount,
    sorting,
    pagination,
    setSorting,
    setPagination,
  });

  const { data } = useQuery({
    queryKey: ['UnassignedDevices'],
    queryFn: () =>
      execute({
        query: unassignedDeviceQuery,
      }),
    select: (data) => data,
    retry: false,
  });

  const { mutateAsync: assignDevices } = useMutation({
    mutationKey: ['AssignDevicesToSite'],
    mutationFn: ({
      siteId,
      deviceIds,
      auditLogInput,
    }: {
      siteId: string;
      deviceIds: string[];
      auditLogInput: AuditLogInsertInput;
    }) => {
      return execute({
        query: assignedDevicesToSiteMutation,
        variables: {
          siteId,
          deviceIds,
          auditLogInput,
        },
      });
    },
    onSuccess: () => {
      toast.success('Devices assigned successfully!', {
        duration: 3000,
      });
    },
  });

  useEffect(() => {
    if (data) {
      setDevices((data.device ?? []) as Device[]);
      setRowCount((data.deviceAggregate?.aggregate?.count as number) ?? 0);
    }
  }, [data]);

  const handleToggleOpenState = () => {
    onToggleOpen();
  };

  const handleAssignDevices = async () => {
    const deviceIds = table
      .getSelectedRowModel()
      .rows.map((row) => row.original.id);
    await assignDevices({
      deviceIds,
      siteId: site.id,
      auditLogInput: createAuditLogInput({
        action: AuditLogAction.ASSIGN_DEVICE_TO_SITE,
        entities: [
          {
            id: site.id,
            entity: AuditLogEntityType.SITE,
          },
          ...deviceIds.map((id) => ({
            id,
            entity: AuditLogEntityType.DEVICE,
          })),
        ],
      }),
    });
    onToggleOpen(true);
  };

  return (
    <Dialog open={open} onOpenChange={handleToggleOpenState}>
      <DialogContent className="sm:max-w-[80vw] lg:max-w-[70vw]">
        <DialogHeader>
          <DialogTitle>Assign Device </DialogTitle>
          <DialogDescription>
            Select devices to assign to the site.
          </DialogDescription>
        </DialogHeader>

        <div className="h-[60vh] overflow-auto">
          <DataTable table={table} selectRowEnabled={true} />
        </div>
        <DialogFooter>
          <div className="mt-4 flex w-full justify-end gap-4">
            <Button
              variant={'default'}
              type="submit"
              size={'sm'}
              onClick={() => void handleAssignDevices()}
            >
              Assign Device
            </Button>

            <Button
              variant={'outline'}
              className="w-30"
              size={'sm'}
              onClick={() => onToggleOpen()}
            >
              Cancel
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
